/**
 * A-3: pure time + count formatters for the browse module.
 *
 * All times render in the viewer's timezone — the source's timezone label
 * never reaches output (inputs are ISO instants; A-2 already discards the
 * wire timezone string). Intl only, no date library.
 */

const MIN = 60 * 1000;

/** @returns {?Date} parsed instant, or null for missing/invalid input. */
const parse = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * "3:00 PM" in the viewer's local timezone.
 * @param {?string} iso ISO 8601 instant with offset
 * @param {string} [timeZone] override for tests; defaults to the viewer's zone
 * @returns {?string}
 */
export function formatLocalTime(iso, timeZone) {
  const d = parse(iso);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone && { timeZone }),
  })
    .format(d)
    .replace(/\u202f/g, ' '); // ICU narrow no-break space → plain space
}

/**
 * Relative countdown badge text: "IN 42 MIN" / "IN 3 HR".
 * Instant math only — timezone-independent.
 * @param {?string} iso ISO 8601 instant with offset
 * @param {number} [now] epoch ms; pass to pin the clock in tests
 * @returns {?string} null when the start time has passed (caller hides the badge)
 */
export function formatCountdown(iso, now = Date.now()) {
  const d = parse(iso);
  if (!d) return null;
  const minutes = Math.ceil((d.getTime() - now) / MIN);
  if (minutes <= 0) return null;
  if (minutes < 60) return `IN ${minutes} MIN`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `IN ${hours} HR`;
  const days = Math.round(hours / 24);
  return `IN ${days} DAY${days === 1 ? '' : 'S'}`;
}

/**
 * Viewer-local calendar date: "Aug 14". Same contract as formatLocalTime.
 * @param {?string} iso
 * @param {string} [timeZone] override for tests
 * @returns {?string}
 */
export function formatLocalDate(iso, timeZone) {
  const d = parse(iso);
  if (!d) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Elapsed-time suffix for the session stats list: "2h 14m ago" / "14m ago".
 * Instant math only — timezone-independent.
 * @param {?string} iso ISO 8601 instant with offset
 * @param {number} [now] epoch ms; pass to pin the clock in tests
 * @returns {?string} null for missing/invalid/future instants
 */
export function formatElapsed(iso, now = Date.now()) {
  const d = parse(iso);
  if (!d) return null;
  const minutes = Math.floor((now - d.getTime()) / MIN);
  if (minutes < 0) return null;
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

/**
 * Concurrent watcher count: "312" / "1.2K". ≥1000 abbreviates.
 * @param {?number} n watchers (concurrent); null = unreported
 * @returns {?string} null when null/undefined (caller hides the pill; 0 is real)
 */
export function formatWatchers(n) {
  if (n == null) return null;
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}
